import firebase from 'firebase/compat/app';
import { useEffect, useState } from 'react';
import { TagStatus } from '../Views/Billing/Table';
import { useAppSelector } from '../hooks';
import { charlotteUid, isAdmin, michaelUid } from 'components/AdminOnly';
import { firestore } from '../firebase';
import { AirCleaner, OfferStatus, PriceType } from 'Views/Agreements/Context/offerReducer';

export interface AppOffer {
  id: string;
  airCleaner: AirCleaner | null;
  createdBy: {
    uid: string;
    displayName: string;
    email: string;
  };
  refId: string; // Generate on client
  finalPrice: number;
  offer: {
    m2: number;
    sensors: number;
    gps: number;
    tablets: number;
    dustMeasurements: number;
    priceType: PriceType;
    discount: number | null;
    sweepingGranules: number;
    discountGranules: number | null;
    note: string | null;
    internalNote: string | null;
    finalPrice?: number | null;
    finalLiter?: number | null;
    deliveryOption?: number;
  };
  acceptTime?: Date;
  declineTime?: Date;
  orgId?: string;
  isDeleted: boolean;
  title: string;
  contact: {
    att: string;
  };
  status: OfferStatus;
  createTime: Date;
  updateTime: Date;
  migratedToDealId?: string;
  migratedToDealAt?: Date | null;
  migratedToDealBy?: {
    uid: string;
    displayName?: string;
    email?: string;
  };
}
export default function useAppOffers() {
  const [offers, setOffers] = useState<AppOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const currentUserId = useAppSelector((state) => state.user.details.uid);
  const admin = isAdmin(currentUserId);
  const isMichael = currentUserId === michaelUid;
  const scopeKey = admin ? 'admin' : isMichael ? 'michael-and-charlotte' : currentUserId;
  const [loadedScopeKey, setLoadedScopeKey] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const mapOffers = (
      querySnapshot: firebase.firestore.QuerySnapshot<firebase.firestore.DocumentData>
    ): AppOffer[] => {
      if (querySnapshot.empty) {
        return [];
      }
      return querySnapshot.docs.flatMap((doc): AppOffer[] => {
        const data = doc.data();
        const createTime = toDateOrNull(data.createTime);
        const updateTime = toDateOrNull(data.updateTime);
        if (!createTime || !updateTime) {
          return [];
        }
        return [
          {
            ...data,
            createTime,
            updateTime,
            acceptTime: toDateOrNull(data.acceptTime) ?? undefined,
            migratedToDealAt: toDateOrNull(data.migratedToDealAt),
            id: doc.id,
            refId: getRefId(data.createdBy?.displayName || '', createTime.getTime()),
          } as AppOffer,
        ];
      });
    };

    setOffers([]);
    setLoading(true);
    setError(null);
    setUpdatedAt(null);

    const normalizeError = (value: unknown) =>
      value instanceof Error ? value : new Error('Ukendt fejl ved indlæsning af tilbud');

    const handleError = (value: unknown) => {
      console.error(value);
      if (!active) return;
      setOffers([]);
      setError(normalizeError(value));
      setUpdatedAt(null);
      setLoading(false);
      setLoadedScopeKey(scopeKey);
    };

    const handleSnapshot = (
      querySnapshot: firebase.firestore.QuerySnapshot<firebase.firestore.DocumentData>,
      onUpdate: (nextOffers: AppOffer[]) => void
    ) => {
      if (!active) return;
      onUpdate(mapOffers(querySnapshot));
      setError(null);
      setUpdatedAt(new Date());
      setLoading(false);
      setLoadedScopeKey(scopeKey);
    };

    const subscribeToCreatorOffers = (
      creatorUid: string,
      onSnapshot: (
        querySnapshot: firebase.firestore.QuerySnapshot<firebase.firestore.DocumentData>
      ) => void,
      onError: (value: unknown) => void
    ): (() => void) => {
      const query = (
        firestore.collection(
          'appOffers'
        ) as firebase.firestore.Query<firebase.firestore.DocumentData>
      )
        .where('createdBy.uid', '==', creatorUid)
        .where('isDeleted', '==', false)
        .orderBy('updateTime', 'desc');

      return query.onSnapshot(onSnapshot, onError);
    };

    if (admin) {
      const query = (
        firestore.collection(
          'appOffers'
        ) as firebase.firestore.Query<firebase.firestore.DocumentData>
      )
        .where('isDeleted', '==', false)
        .orderBy('updateTime', 'desc');

      const unsubscribe = query.onSnapshot(
        (querySnapshot) => handleSnapshot(querySnapshot, setOffers),
        handleError
      );
      return () => {
        active = false;
        unsubscribe();
      };
    }

    if (isMichael) {
      type SourceState = {
        offers: AppOffer[];
        ready: boolean;
        error: Error | null;
        updatedAt: Date | null;
      };
      const sources: Record<'michael' | 'charlotte', SourceState> = {
        michael: { offers: [], ready: false, error: null, updatedAt: null },
        charlotte: { offers: [], ready: false, error: null, updatedAt: null },
      };

      const publish = () => {
        if (!active) return;
        setOffers(
          [...sources.michael.offers, ...sources.charlotte.offers].sort(
            (a, b) => b.updateTime.getTime() - a.updateTime.getTime()
          )
        );
        setLoading(!sources.michael.ready || !sources.charlotte.ready);
        setError(sources.michael.error || sources.charlotte.error);
        const timestamps = [sources.michael.updatedAt, sources.charlotte.updatedAt].filter(
          (value): value is Date => Boolean(value)
        );
        setUpdatedAt(
          timestamps.length > 0
            ? new Date(Math.min(...timestamps.map((value) => value.getTime())))
            : null
        );
        if (sources.michael.ready && sources.charlotte.ready) {
          setLoadedScopeKey(scopeKey);
        }
      };

      const handleSourceSnapshot = (
        source: 'michael' | 'charlotte',
        querySnapshot: firebase.firestore.QuerySnapshot<firebase.firestore.DocumentData>
      ) => {
        sources[source] = {
          offers: mapOffers(querySnapshot),
          ready: true,
          error: null,
          updatedAt: new Date(),
        };
        publish();
      };
      const handleSourceError = (source: 'michael' | 'charlotte', value: unknown) => {
        console.error(value);
        sources[source] = {
          offers: [],
          ready: true,
          error: normalizeError(value),
          updatedAt: null,
        };
        publish();
      };

      const unsubscribeMichael = subscribeToCreatorOffers(
        michaelUid,
        (snapshot) => handleSourceSnapshot('michael', snapshot),
        (value) => handleSourceError('michael', value)
      );
      const unsubscribeCharlotte = subscribeToCreatorOffers(
        charlotteUid,
        (snapshot) => handleSourceSnapshot('charlotte', snapshot),
        (value) => handleSourceError('charlotte', value)
      );

      return () => {
        active = false;
        unsubscribeMichael();
        unsubscribeCharlotte();
      };
    }

    const unsubscribe = subscribeToCreatorOffers(
      currentUserId,
      (querySnapshot) => handleSnapshot(querySnapshot, setOffers),
      handleError
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, [admin, currentUserId, isMichael, scopeKey]);

  const hasLoadedCurrentScope = loadedScopeKey === scopeKey;

  return {
    offers: hasLoadedCurrentScope ? offers : [],
    loading: !hasLoadedCurrentScope || (!error && (loading || updatedAt === null)),
    error: hasLoadedCurrentScope ? error : null,
    updatedAt: hasLoadedCurrentScope ? updatedAt : null,
  };
}

function toDateOrNull(value: any): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value?.toDate === 'function') {
    return value.toDate();
  }
  return null;
}

export function getRefId(name: string, time: number) {
  const user = getFirstThreeInitials(name);
  return `${user}-${time}`;
}

function getFirstThreeInitials(name: string) {
  const [a, b, c] = name.split('');
  return `${a || ''}${b || ''}${c || ''}`.toUpperCase();
}
